import { useDepartmentText } from 'model/DepartmentTerminology';
import { Box, Collapse, Divider, Flex, Group, Modal, Stack, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconBuildingSkyscraper, IconChevronDown, IconMapPinPlus, IconWorld } from '@tabler/icons-react';
import { DepartmentBreadcrumbItem } from 'components/department/DepartmentBreadcrumb';
import DepartmentParentReference from 'components/department/DepartmentParentReference';
import P2Regular from 'components/typography/P2Regular';
import P3Medium from 'components/typography/P3Medium';
import { UserRow } from 'components/tables/data/user/types';
import useLocalStorage from 'hooks/use-local-storage';
import { usePermissionDataProvider } from 'model/PermissionDataContext';
import { ROLE_ADMIN_ID } from 'model/Role';
import { useMemo } from 'react';

type PermissionSummary = UserRow['permissions']['summary'][number];
type PermissionDetailSection = 'global' | 'organization' | 'department';

interface PermissionDetailSummary extends PermissionSummary {
  permissionGroup: string;
}

interface PermissionScopeEntityRow {
  entityId: number;
  title: string;
  subtitle?: string;
  breadcrumbs?: DepartmentBreadcrumbItem[];
  permissions: PermissionDetailSummary[];
}

interface PermissionSectionDefinition {
  value: PermissionDetailSection;
  label: string;
  count: number;
}

interface PermissionDetailSectionState {
  global: boolean;
  organization: boolean;
  department: boolean;
}

const permissionDetailSectionStorageKey = 'objekto.user-permissions-detail.sections';

/**
 * Renders a single scope counter inside the permission summary.
 */
function PermissionScopeCounter({ kind, count }: { kind: 'global' | 'organization' | 'department'; count: number }) {
  const iconProps = {
    size: 14,
    stroke: 1.7,
    color: '#353746',
  };

  const label =
    kind === 'global'
      ? `global permission count ${count}`
      : kind === 'organization'
      ? `organization permission count ${count}`
      : `department permission count ${count}`;

  return (
    <Group spacing={4} align="center" aria-label={label} noWrap>
      {kind === 'global' && <IconWorld {...iconProps} />}
      {kind === 'department' && <IconMapPinPlus {...iconProps} />}
      {kind === 'organization' && <IconBuildingSkyscraper {...iconProps} />}
      <P3Medium color="gray.8">{count}</P3Medium>
    </Group>
  );
}

/**
 * Renders compact scope markers for a permission inside the tooltip.
 */
function PermissionTooltipScopeMarkers({ summary }: { summary: PermissionSummary }) {
  return (
    <Group spacing={6} align="center" noWrap>
      {summary.global && (
        <Group spacing={2} align="center" aria-label="global scope">
          <IconWorld size={14} stroke={1.7} color="#353746" />
        </Group>
      )}
      {summary.departments.length > 0 && (
        <PermissionScopeCounter kind="department" count={summary.departments.length} />
      )}
      {summary.organizations.length > 0 && (
        <PermissionScopeCounter kind="organization" count={summary.organizations.length} />
      )}
    </Group>
  );
}

/**
 * Renders a compact permission badge.
 */
function PermissionNameBadge({ label }: { label: string }) {
  return (
    <Box
      px={5}
      py={1}
      bg="gray.0"
      sx={{
        borderRadius: 999,
        border: '1px solid #E9ECEF',
        lineHeight: 1.1,
        width: 'fit-content',
      }}
    >
      <P2Regular color="gray.8">{label}</P2Regular>
    </Box>
  );
}

/**
 * Renders a compact numeric badge.
 */
function CompactCountBadge({ count }: { count: number }) {
  return (
    <Box
      px={5}
      py={1}
      bg="gray.1"
      sx={{
        borderRadius: 999,
        border: '1px solid #E9ECEF',
        lineHeight: 1,
        width: 'fit-content',
      }}
    >
      <P2Regular color="gray.8">{count}</P2Regular>
    </Box>
  );
}

/**
 * Renders a section label with an attached count badge.
 */
function PermissionSectionLabel({ label, count }: { label: string; count: number }) {
  return (
    <Group spacing={8} noWrap>
      <P2Regular color="gray.8">{label}</P2Regular>
      <CompactCountBadge count={count} />
    </Group>
  );
}

/**
 * Renders a number badge with an accessibility label.
 */
function PermissionCountBadge({ label, count }: { label: string; count: number }) {
  return (
    <Box aria-label={label}>
      <CompactCountBadge count={count} />
    </Box>
  );
}

/**
 * Derives grouped tooltip items and modal tab data from permission summary items.
 */
function usePermissionDetailData(permissionSummary: PermissionSummary[]) {
  const { getPermission } = usePermissionDataProvider();

  return useMemo(() => {
    const detailSummary = permissionSummary
      .map((summary) => ({
        ...summary,
        permissionGroup: getPermission(summary.permissionId)?.permissionGroup ?? 'Ostatné',
      }))
      .sort((left, right) => {
        if (left.permissionGroup !== right.permissionGroup) {
          return left.permissionGroup.localeCompare(right.permissionGroup);
        }

        return left.permissionName.localeCompare(right.permissionName);
      });

    const groups = new Map<string, PermissionDetailSummary[]>();
    const organizationRows = new Map<number, PermissionScopeEntityRow>();
    const departmentRows = new Map<number, PermissionScopeEntityRow>();

    detailSummary.forEach((summary) => {
      const permissionGroup = summary.permissionGroup;

      if (!groups.has(permissionGroup)) {
        groups.set(permissionGroup, []);
      }

      groups.get(permissionGroup)!.push(summary);

      summary.organizations.forEach((organization) => {
        if (!organizationRows.has(organization.organizationId)) {
          organizationRows.set(organization.organizationId, {
            entityId: organization.organizationId,
            title: organization.organizationName,
            permissions: [],
          });
        }

        organizationRows.get(organization.organizationId)!.permissions.push(summary);
      });

      summary.departments.forEach((department) => {
        if (!departmentRows.has(department.departmentId)) {
          departmentRows.set(department.departmentId, {
            entityId: department.departmentId,
            title: department.departmentName,
            subtitle: department.organizationName,
            breadcrumbs: department.breadcrumbs,
            permissions: [],
          });
        }

        departmentRows.get(department.departmentId)!.permissions.push(summary);
      });
    });

    /**
     * Sorts scope rows and their permission lists for stable display.
     */
    const sortRows = (rows: PermissionScopeEntityRow[]) =>
      rows
        .map((row) => ({
          ...row,
          permissions: row.permissions.sort((left, right) => {
            if (left.permissionGroup !== right.permissionGroup) {
              return left.permissionGroup.localeCompare(right.permissionGroup);
            }

            return left.permissionName.localeCompare(right.permissionName);
          }),
        }))
        .sort((left, right) => {
          const titleComparison = left.title.localeCompare(right.title);

          if (titleComparison !== 0) {
            return titleComparison;
          }

          return (left.subtitle ?? '').localeCompare(right.subtitle ?? '');
        });

    return {
      groupedPermissions: Array.from(groups.entries()).map(([permissionGroup, items]) => ({
        permissionGroup,
        items,
      })),
      globalPermissions: detailSummary.filter((summary) => summary.global),
      organizationRows: sortRows(Array.from(organizationRows.values())),
      departmentRows: sortRows(Array.from(departmentRows.values())),
    };
  }, [getPermission, permissionSummary]);
}

/**
 * Renders tooltip content with permissions grouped by permission group.
 */
function PermissionSummaryTooltip({ permissionSummary }: { permissionSummary: PermissionSummary[] }) {
  const { groupedPermissions } = usePermissionDetailData(permissionSummary);

  return (
    <Stack spacing={10}>
      {groupedPermissions.map(({ permissionGroup, items }) => (
        <Stack spacing={6} key={permissionGroup}>
          <P3Medium color="gray.9">{permissionGroup}</P3Medium>
          {items.map((summary) => (
            <Group key={summary.permissionId} position="apart" spacing={12} noWrap>
              <P2Regular color="gray.8">{summary.permissionName}</P2Regular>
              <PermissionTooltipScopeMarkers summary={summary} />
            </Group>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

/**
 * Calculates the visible scope counters for the permission summary.
 */
function usePermissionScopeCounts(permissionSummary: PermissionSummary[]) {
  return useMemo(() => {
    const globalPermissionCount = permissionSummary.filter((summary) => summary.global).length;
    const organizationIds = new Set<number>();
    const departmentIds = new Set<number>();

    permissionSummary.forEach((summary) => {
      summary.organizations.forEach((organization) => {
        organizationIds.add(organization.organizationId);
      });

      summary.departments.forEach((department) => {
        departmentIds.add(department.departmentId);
      });
    });

    return {
      globalPermissionCount,
      organizationCount: organizationIds.size,
      departmentCount: departmentIds.size,
    };
  }, [permissionSummary]);
}

/**
 * Builds the visible detail sections and their counters.
 */
function usePermissionDetailSections(permissionSummary: PermissionSummary[]) {
  const departmentText = useDepartmentText();
  const { globalPermissions, organizationRows, departmentRows } = usePermissionDetailData(permissionSummary);

  return useMemo(() => {
    const sections = [
      {
        value: 'global',
        label: 'Globálne',
        count: globalPermissions.length,
      },
      {
        value: 'organization',
        label: 'Organizácie',
        count: organizationRows.length,
      },
      {
        value: 'department',
        label: departmentText('Položky úrovne', 'Strediská'),
        count: departmentRows.length,
      },
    ] as PermissionSectionDefinition[];

    const visibleSections = sections.filter((section) => section.count > 0);

    return {
      sections: visibleSections,
      globalPermissions,
      organizationRows,
      departmentRows,
    };
  }, [departmentText, departmentRows, globalPermissions, organizationRows]);
}

/**
 * Renders a collapsible detail section inside the permissions modal.
 */
function PermissionDetailSectionPanel({
  value,
  label,
  count,
  expanded,
  onToggle,
  children,
}: {
  value: PermissionDetailSection;
  label: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        borderRadius: 8,
        border: '1px solid #E9ECEF',
        backgroundColor: '#FFFFFF',
      }}
    >
      <UnstyledButton
        aria-expanded={expanded}
        aria-controls={`permission-detail-section-${value}`}
        onClick={onToggle}
        sx={{
          width: '100%',
          display: 'block',
        }}
      >
        <Flex px={10} py={8} align="center" justify="space-between" gap={12}>
          <PermissionSectionLabel label={label} count={count} />
          <IconChevronDown
            size={16}
            stroke={1.8}
            color="#495057"
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 150ms ease',
              flexShrink: 0,
            }}
          />
        </Flex>
      </UnstyledButton>
      <Collapse in={expanded}>
        <Divider color="gray.1" />
        <Box id={`permission-detail-section-${value}`} px={10} py={8}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}

/**
 * Renders rows for the organization and department detail tabs.
 */
export function PermissionScopeEntityList({
  rows,
  emptyLabel,
}: {
  rows: PermissionScopeEntityRow[];
  emptyLabel: string;
}) {
  if (rows.length === 0) {
    return <P2Regular color="gray.7">{emptyLabel}</P2Regular>;
  }

  return (
    <Stack spacing={4} mt={8}>
      {rows.map((row) => (
        <Box
          key={row.entityId}
          p={6}
          sx={{
            borderRadius: 6,
            border: '1px solid #F1F3F5',
            backgroundColor: '#F8F9FA',
          }}
        >
          <Flex gap={8} align="flex-start" wrap="wrap">
            <Flex gap={6} align="center" wrap="wrap" sx={{ flexShrink: 0 }}>
              {row.breadcrumbs && <DepartmentParentReference breadcrumbs={row.breadcrumbs} />}
              <P3Medium color="gray.9">{row.title}</P3Medium>
              {row.subtitle && <P2Regular color="gray.7">({row.subtitle})</P2Regular>}
              <PermissionCountBadge
                label={`${row.subtitle ? 'department' : 'organization'} permission count ${row.title} ${
                  row.permissions.length
                }`}
                count={row.permissions.length}
              />
            </Flex>
            <Flex gap={4} wrap="wrap" sx={{ flex: 1, minWidth: 240 }}>
              {row.permissions.map((permission) => (
                <PermissionNameBadge
                  key={`${row.entityId}-${permission.permissionId}`}
                  label={permission.permissionName}
                />
              ))}
            </Flex>
          </Flex>
        </Box>
      ))}
    </Stack>
  );
}

/**
 * Renders the modal with detailed permission scope sections.
 */
function PermissionDetailsModal({
  opened,
  onClose,
  permissionSummary,
}: {
  opened: boolean;
  onClose: () => void;
  permissionSummary: PermissionSummary[];
}) {
  const departmentText = useDepartmentText();
  const { sections, globalPermissions, organizationRows, departmentRows } =
    usePermissionDetailSections(permissionSummary);
  const [sectionState, , updateSectionState] = useLocalStorage<PermissionDetailSectionState>(
    permissionDetailSectionStorageKey,
    {
      global: true,
      organization: true,
      department: true,
    }
  );

  /**
   * Toggles the open state of a single detail section.
   */
  function toggleSection(section: PermissionDetailSection) {
    updateSectionState({
      [section]: !sectionState[section],
    });
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Detail oprávnení"
      centered
      size="70rem"
      styles={{
        content: {
          maxWidth: 'calc(100vw - 32px)',
        },
        body: {
          paddingTop: 4,
        },
      }}
    >
      <Divider color="gray.1" mb={8} />
      <Stack spacing={8}>
        {sections.map((section) => {
          if (section.value === 'global') {
            return (
              <PermissionDetailSectionPanel
                key={section.value}
                value={section.value}
                label={section.label}
                count={section.count}
                expanded={sectionState.global}
                onToggle={() => toggleSection(section.value)}
              >
                <Flex gap={4} wrap="wrap">
                  {globalPermissions.map((permission) => (
                    <PermissionNameBadge key={permission.permissionId} label={permission.permissionName} />
                  ))}
                </Flex>
              </PermissionDetailSectionPanel>
            );
          }

          if (section.value === 'organization') {
            return (
              <PermissionDetailSectionPanel
                key={section.value}
                value={section.value}
                label={section.label}
                count={section.count}
                expanded={sectionState.organization}
                onToggle={() => toggleSection(section.value)}
              >
                <PermissionScopeEntityList
                  rows={organizationRows}
                  emptyLabel="Používateľ nemá žiadne organizačné oprávnenia."
                />
              </PermissionDetailSectionPanel>
            );
          }

          return (
            <PermissionDetailSectionPanel
              key={section.value}
              value={section.value}
              label={section.label}
              count={section.count}
              expanded={sectionState.department}
              onToggle={() => toggleSection(section.value)}
            >
              <PermissionScopeEntityList
                rows={departmentRows}
                emptyLabel={departmentText(
                  'Používateľ nemá žiadne oprávnenia v položkách úrovne.',
                  'Používateľ nemá žiadne strediskové oprávnenia.'
                )}
              />
            </PermissionDetailSectionPanel>
          );
        })}
      </Stack>
    </Modal>
  );
}

/**
 * Column for collapsed user permission scope summaries.
 */
export default function PermissionsColumn({ data: { permissions, role } }: { data: UserRow }) {
  const permissionSummary = permissions.summary ?? [];
  const { globalPermissionCount, organizationCount, departmentCount } = usePermissionScopeCounts(permissionSummary);
  const [detailOpened, { close: closeDetail, open: openDetail }] = useDisclosure(false);

  if (role.roleId === ROLE_ADMIN_ID) {
    return <P2Regular>Systémový administrátor</P2Regular>;
  }

  if (permissionSummary.length === 0) {
    return <P2Regular>Používateľ nemá žiadne oprávnenia</P2Regular>;
  }

  return (
    <>
      <Tooltip
        withArrow
        withinPortal
        multiline
        bg="gray.2"
        label={<PermissionSummaryTooltip permissionSummary={permissionSummary} />}
      >
        <UnstyledButton
          aria-label="permission scope summary"
          onClick={() => {
            openDetail();
          }}
        >
          <Flex
            gap={10}
            px={10}
            py={4}
            align="center"
            wrap="nowrap"
            bg="gray.1"
            sx={{
              borderRadius: 999,
              border: '1px solid #E9ECEF',
              cursor: 'pointer',
              width: 'fit-content',
            }}
          >
            {globalPermissionCount > 0 && <PermissionScopeCounter kind="global" count={globalPermissionCount} />}
            {departmentCount > 0 && <PermissionScopeCounter kind="department" count={departmentCount} />}
            {organizationCount > 0 && <PermissionScopeCounter kind="organization" count={organizationCount} />}
          </Flex>
        </UnstyledButton>
      </Tooltip>
      <PermissionDetailsModal opened={detailOpened} onClose={closeDetail} permissionSummary={permissionSummary} />
    </>
  );
}
